import React from 'react';
import { ServiceType } from '../types/analysis.types';
import { YearColumn } from '../../../hooks/analysis/useYearComparison';
import { VISITS_METRIC, YearTotals, changePct, formatChange } from '../utils/yearComparison';

interface YearComparisonTableProps {
    columns: YearColumn[];
    serviceTypes: ServiceType[];
    /** The metric the chart shows; its row is highlighted */
    chartedMetric: string;
    /** Clicking a row charts it */
    onChartMetric: (metric: string) => void;
}

interface MetricRow {
    key: string;
    label: string;
    /** Can be shown in the chart (all but Byggnader med besök) */
    chartable: boolean;
    value: (totals: YearTotals) => number;
}

const formatNumber = (value: number): string => value.toLocaleString('sv-SE', { maximumFractionDigits: 2 });

/** Green for an increase, red for a decrease, neutral for none (rounded) or nothing to compare */
const changeClass = (pct: number | null): string => {
    const rounded = pct === null ? 0 : Math.round(pct);
    if (rounded > 0) return 'year-comparison-change year-comparison-change--up';
    if (rounded < 0) return 'year-comparison-change year-comparison-change--down';
    return 'year-comparison-change';
};

/** Metrics × years; each year after the first is followed by its change against the year before it */
const YearComparisonTable: React.FC<YearComparisonTableProps> = ({ columns, serviceTypes, chartedMetric, onChartMetric }) => {
    const rows: MetricRow[] = [
        { key: VISITS_METRIC, label: 'Besök', chartable: true, value: totals => totals.visits },
        { key: 'buildings', label: 'Byggnader med besök', chartable: false, value: totals => totals.buildingsWithVisits },
        ...serviceTypes
            .filter((type): type is ServiceType & { key: string } => Boolean(type.key))
            .map(type => ({
                key: type.key,
                label: type.unit ? `${type.title || type.key} (${type.unit})` : (type.title || type.key),
                chartable: true,
                value: (totals: YearTotals) => totals.byService[type.key] ?? 0,
            })),
    ];

    return (
        <div className="year-comparison-table-scroll">
            <table className="year-comparison-table">
                <thead>
                <tr>
                    <th>Mått</th>
                    {columns.map((column, index) => (
                        <React.Fragment key={column.year}>
                            <th className="year-comparison-number">{column.year}</th>
                            {index > 0 && (
                                <th className="year-comparison-number" title={`${column.year} jämfört med ${columns[index - 1].year}`}>
                                    Förändring
                                </th>
                            )}
                        </React.Fragment>
                    ))}
                </tr>
                </thead>
                <tbody>
                {rows.map(row => (
                    <tr
                        key={row.key}
                        className={[
                            row.chartable ? 'year-comparison-row--chartable' : '',
                            row.key === chartedMetric ? 'year-comparison-row--charted' : '',
                        ].join(' ').trim() || undefined}
                        onClick={row.chartable ? () => onChartMetric(row.key) : undefined}
                    >
                        <td>
                            {row.chartable ? (
                                // The row is clickable; the button gives keyboard and screen reader access
                                <button
                                    type="button"
                                    className="year-comparison-row-button"
                                    aria-pressed={row.key === chartedMetric}
                                    onClick={event => { event.stopPropagation(); onChartMetric(row.key); }}
                                >
                                    {row.label}
                                </button>
                            ) : row.label}
                        </td>
                        {columns.map((column, index) => {
                            const value = row.value(column.totals);
                            const previous = index > 0 ? row.value(columns[index - 1].totals) : null;
                            const change = previous === null ? null : changePct(previous, value);
                            return (
                                <React.Fragment key={column.year}>
                                    <td className="year-comparison-number">{formatNumber(value)}</td>
                                    {index > 0 && (
                                        <td className={`year-comparison-number ${changeClass(change)}`}>
                                            {change === null ? '–' : formatChange(change)}
                                        </td>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default YearComparisonTable;
